import { Fragment, type ReactNode } from 'react'
import { InfoPopover } from '@/components/InfoPopover'
import { cn } from '@/lib/utils'
import { MONO, fmt } from './format'
import type { PLSubtotal } from '../types'

export const SUBTOTALS_V1_NOTICE =
  'This data bundle predates the Business unit › Department › Category view. Upload a refresh built with the current export (schema v2) to see it.'

interface IncomeSubtotalRowsProps {
  /** Unit subtotals, sliced to `months`. Undefined = a v1 bundle (notice row). */
  units: PLSubtotal[] | undefined
  departments: PLSubtotal[] | undefined
  /** Income account id → display name, listed on hover of each subtotal row. */
  accountNames: Record<string, string>
  months: string[]
  currency: string
}

/**
 * Income by business unit › department — table rows that sit directly under the
 * Income row of a P&L statement (and of the Consolidated income card). A pure
 * regrouping of the Income accounts: the unit rows add up to Income every month,
 * and each unit's department rows add up to the unit. Unit rows are bold, tinted
 * and not clickable (the Sales Report's unit treatment); department rows are
 * indented beneath their unit and list their accounts on hover. A unit with no
 * departments (US "Other products (unsplit) / bridge") is shown at unit level.
 */
export function IncomeSubtotalRows({ units, departments, accountNames, months, currency }: IncomeSubtotalRowsProps) {
  if (!units) {
    return (
      <tr data-pl-row="subtotal-notice" className="border-t border-stone-50 dark:border-stone-800/40">
        <td colSpan={months.length + 2} className="p-0">
          <p className="sticky left-0 max-w-2xl py-2 pl-9 pr-4 text-xs text-stone-500 dark:text-stone-400">
            {SUBTOTALS_V1_NOTICE}
          </p>
        </td>
      </tr>
    )
  }

  const accountsTitle = (s: PLSubtotal) =>
    s.accounts?.length ? `Accounts: ${s.accounts.map((id) => accountNames[id] ?? id).join(' · ')}` : undefined
  const dash = <span className="text-stone-300 dark:text-stone-600">—</span>
  const valueAt = (s: PLSubtotal, i: number) => s.values[i] ?? 0

  return (
    <>
      <IncomeCaptionRow colSpan={months.length + 1}>
        By business unit
        <InfoPopover term="businessUnit" />
        <span aria-hidden>›</span>
        department
        <InfoPopover term="department" />
      </IncomeCaptionRow>
      {units.map((u) => {
        const unitDepartments = (departments ?? []).filter((d) => d.unit === u.id)
        return (
          <Fragment key={u.id}>
            <tr data-pl-row="unit" data-unit-id={u.id} className="border-t border-stone-100 bg-stone-50/70 dark:border-stone-800/70 dark:bg-stone-800/40">
              {/* Opaque base under the tint so figures never show through the sticky column. */}
              <th scope="row" className="sticky left-0 z-10 bg-white p-0 text-left dark:bg-stone-900">
                <span
                  className="block truncate bg-stone-50/70 py-2 pl-9 pr-4 text-xs font-semibold text-stone-900 dark:bg-stone-800/40 dark:text-stone-50"
                  title={accountsTitle(u)}
                >
                  {u.name}
                </span>
              </th>
              {months.map((_m, i) => {
                const v = valueAt(u, i)
                return (
                  <td key={i} className={cn('whitespace-nowrap px-2 py-2 text-right text-xs font-semibold text-stone-800 dark:text-stone-100', MONO)}>
                    {v === 0 ? dash : fmt(v, currency)}
                  </td>
                )
              })}
              <td className={cn('whitespace-nowrap px-3 py-2 text-right text-xs font-bold text-stone-900 dark:text-stone-50', MONO)}>
                {u.ytd === 0 ? dash : fmt(u.ytd, currency)}
              </td>
            </tr>
            {unitDepartments.map((d) => (
              <tr
                key={d.id}
                data-pl-row="department"
                data-unit-id={u.id}
                data-department-id={d.id}
                className="border-t border-stone-50 dark:border-stone-800/40"
              >
                <th scope="row" className="sticky left-0 z-10 bg-white py-1.5 pl-14 pr-4 text-left dark:bg-stone-900">
                  <span className="block truncate text-xs font-normal text-stone-600 dark:text-stone-300" title={accountsTitle(d)}>
                    {d.name}
                  </span>
                </th>
                {months.map((_m, i) => {
                  const v = valueAt(d, i)
                  return (
                    <td key={i} className={cn('whitespace-nowrap px-2 py-1.5 text-right text-xs text-stone-600 dark:text-stone-300', MONO)}>
                      {v === 0 ? dash : fmt(v, currency)}
                    </td>
                  )
                })}
                <td className={cn('whitespace-nowrap px-3 py-1.5 text-right text-xs font-medium text-stone-700 dark:text-stone-200', MONO)}>
                  {d.ytd === 0 ? dash : fmt(d.ytd, currency)}
                </td>
              </tr>
            ))}
          </Fragment>
        )
      })}
    </>
  )
}

/** A quiet label row inside the Income section ("By business unit › department",
 *  "By account"). The first cell stays sticky with the statement's first column. */
export function IncomeCaptionRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr data-pl-row="caption" className="border-t border-stone-50 dark:border-stone-800/40">
      <th scope="row" className="sticky left-0 z-10 bg-white pb-1 pl-9 pr-4 pt-2 text-left dark:bg-stone-900">
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-stone-400 dark:text-stone-500">{children}</span>
      </th>
      <td colSpan={colSpan} />
    </tr>
  )
}
